import {
  Button,
  Link,
  ListBox,
  Modal,
  Pagination,
  SearchField,
  Select,
  Spinner,
  Table,
  Tooltip,
  toast,
} from "@heroui/react";
import { useEffect, useState } from "react";
import { AlertCircle, Calendar, CheckCircle2, Clock, Copy, HelpCircle, User } from "lucide-react";
import {
  TemplatePtAPI,
  DecodeTemplateStatus,
  type DecodeTemplateRecord,
  type TraceDecodeResult,
} from "@repo/api";
import ComponentDynamicUser from "../components/ComponentUser";
import { useDecodeHistory } from "./use-trace";
import { formatTime } from "@repo/utils/tools";

const PAGE_SIZE = 10;

const STATUS_FILTER_OPTIONS = [
  { id: "all", label: "全部状态" },
  { id: String(DecodeTemplateStatus.Processing), label: "处理中" },
  { id: String(DecodeTemplateStatus.Completed), label: "已完成" },
  { id: String(DecodeTemplateStatus.Failed), label: "失败" },
] as const;

const COLUMNS = [
  { key: "decode_id", label: "编号" },
  { key: "file_name", label: "文件名" },
  { key: "created_at", label: "上传日期" },
  { key: "status", label: "状态" },
  { key: "file_type", label: "文件类型" },
  { key: "result", label: "处理结果" },
] as const;

type DecodeHistoryProps = {
  enabled?: boolean;
  className?: string;
};

function getFileTypeLabel(fileName: string): string {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".psd")) return "PSD";
  if (/\.(png|jpe?g|webp|gif)$/.test(lower)) return "图片";
  const dot = fileName.lastIndexOf(".");
  return dot > 0 ? fileName.slice(dot + 1).toUpperCase() : "-";
}

function isProcessing(status: number) {
  return status === DecodeTemplateStatus.Processing;
}

function isViewable(status: number) {
  return status === DecodeTemplateStatus.Completed || status === DecodeTemplateStatus.Failed;
}

function buildPageItems(current: number, total: number): Array<number | "ellipsis"> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: Array<number | "ellipsis"> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) pages.push("ellipsis");
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push("ellipsis");
  pages.push(total);
  return pages;
}

/**
 * 查标历史表格（web / webview 共用）。
 * 列表走 React Query：筛选 / 翻页进 key；有处理中记录时 30s 自动轮询；
 * 提交新解码后由 TemplateTracePanel 失效缓存刷新，不再需要 refreshKey。
 */
export default function DecodeHistory({ enabled = true, className }: DecodeHistoryProps) {
  const [page, setPage] = useState(1);

  const [fileNameInput, setFileNameInput] = useState("");
  const [decodeIdInput, setDecodeIdInput] = useState("");
  const [fileName, setFileName] = useState("");
  const [decodeId, setDecodeId] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const [resultOpen, setResultOpen] = useState(false);
  const [activeRecord, setActiveRecord] = useState<DecodeTemplateRecord | null>(null);
  const [resultLoading, setResultLoading] = useState(false);
  const [resultError, setResultError] = useState("");
  const [results, setResults] = useState<TraceDecodeResult[]>([]);

  const listQuery = useDecodeHistory(
    {
      page,
      page_size: PAGE_SIZE,
      file_name: fileName || undefined,
      decode_id: decodeId || undefined,
      status: statusFilter === "all" ? undefined : Number(statusFilter),
    },
    enabled,
  );
  const items = listQuery.data?.content ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, listQuery.data?.total_pages ?? 1);
  // 翻页时保留上一页数据（keepPreviousData），只在首屏显示加载态
  const loading = enabled && listQuery.isPending;

  useEffect(() => {
    if (listQuery.error) {
      const err = listQuery.error as { msg?: string; message?: string };
      toast.danger(err?.msg || err?.message || "获取查标历史失败");
    }
  }, [listQuery.error]);

  const applyFileNameSearch = () => {
    setPage(1);
    setFileName(fileNameInput.trim());
  };

  const applyDecodeIdSearch = () => {
    setPage(1);
    setDecodeId(decodeIdInput.trim());
  };

  const handleCopyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(String(id));
      toast.success("编号已复制至粘贴板");
    } catch {
      toast.danger("复制失败，请手动再试");
    }
  };

  const openResultModal = async (record: DecodeTemplateRecord) => {
    if (!isViewable(record.status)) return;

    setActiveRecord(record);
    setResultOpen(true);
    setResultLoading(true);
    setResultError("");
    setResults([]);

    try {
      const response = await TemplatePtAPI.queryDecodeResult(record.decode_id);

      if (response.status) {
        throw new Error(response.msg || "获取处理结果失败");
      }

      if (response.data?.wait) {
        setResultError("结果仍在处理中，请稍后再试");
        return;
      }

      if (response.data?.error) {
        setResultError(response.data.error);
        return;
      }

      if (record.error_msg && (!response.data?.data || response.data.data.length === 0)) {
        setResultError(record.error_msg);
        return;
      }

      setResults(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error: unknown) {
      const err = error as { msg?: string; message?: string };
      setResultError(err?.msg || err?.message || "获取处理结果失败");
    } finally {
      setResultLoading(false);
    }
  };

  const pageItems = buildPageItems(page, totalPages);
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <div className={`flex flex-col gap-4 rounded-lg bg-white px-6 py-6 ${className ?? ""}`}>
      <h2 className="text-xl font-bold text-black">查标历史</h2>

      <div className="flex flex-col items-stretch gap-2 lg:flex-row lg:items-start">
        <SearchField
          variant="secondary"
          aria-label="文件名搜索"
          value={fileNameInput}
          onChange={(value) => {
            setFileNameInput(value);
            if (value === "") setFileName("");
          }}
          onSubmit={applyFileNameSearch}
          className="w-full lg:w-70"
        >
          <SearchField.Group>
            <SearchField.SearchIcon />
            <SearchField.Input placeholder="文件名搜索" />
            <SearchField.ClearButton />
          </SearchField.Group>
        </SearchField>

        <SearchField
          variant="secondary"
          aria-label="编号搜索"
          value={decodeIdInput}
          onChange={(value) => {
            setDecodeIdInput(value);
            if (value === "") setDecodeId("");
          }}
          onSubmit={applyDecodeIdSearch}
          className="w-full lg:w-70"
        >
          <SearchField.Group>
            <SearchField.SearchIcon />
            <SearchField.Input placeholder="编号搜索" />
            <SearchField.ClearButton />
          </SearchField.Group>
        </SearchField>

        <Select
          variant="secondary"
          aria-label="结果筛选"
          className="w-full lg:w-60"
          value={statusFilter}
          onChange={(key) => {
            setPage(1);
            setStatusFilter(String(key ?? "all"));
          }}
        >
          <Select.Trigger>
            <Select.Value className="ml-auto text-muted" />
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              {STATUS_FILTER_OPTIONS.map((option) => (
                <ListBox.Item key={option.id} id={option.id} textValue={option.label}>
                  {option.label}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
              ))}
            </ListBox>
          </Select.Popover>
        </Select>
      </div>

      <Table className="w-full overflow-hidden" variant="secondary">
        <Table.ScrollContainer>
          <Table.Content aria-label="查标历史">
            <Table.Header columns={[...COLUMNS]}>
              {(column) => (
                <Table.Column isRowHeader={column.key === "decode_id"} key={column.key}>
                  {column.label}
                </Table.Column>
              )}
            </Table.Header>
            <Table.Body
              items={items}
              renderEmptyState={() =>
                loading ? (
                  <div className="flex items-center justify-center py-10">
                    <Spinner />
                  </div>
                ) : (
                  <div className="py-10 text-center text-muted">暂无查标记录</div>
                )
              }
            >
              {(item) => (
                <Table.Row id={String(item.id)}>
                  <Table.Cell>
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-sm text-foreground">{item.decode_id}</span>
                      <button
                        type="button"
                        className="shrink-0 text-muted hover:text-foreground"
                        aria-label={`复制编号 ${item.decode_id}`}
                        onClick={() => handleCopyId(item.decode_id)}
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="break-all text-sm text-foreground">{item.file_name || "-"}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-sm text-foreground">
                      {formatTime(item.created_at, "YYYY-MM-DD HH:mm:ss")}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    {isProcessing(item.status) ? (
                      <div className="flex items-center gap-2 text-sm text-foreground">
                        <Spinner size="sm" color="current" className="text-primary" />
                        <span>处理中</span>
                      </div>
                    ) : item.status === DecodeTemplateStatus.Failed ? (
                      <span className="text-sm text-danger">失败</span>
                    ) : (
                      <span className="text-sm text-success">已完成</span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-foreground">{getFileTypeLabel(item.file_name)}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <Link
                      className="text-sm underline-offset-2"
                      isDisabled={!isViewable(item.status)}
                      onPress={() => openResultModal(item)}
                    >
                      查看
                    </Link>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Content>
        </Table.ScrollContainer>
      </Table>

      <Pagination size="sm" className="w-full flex-wrap justify-between gap-3">
        <Pagination.Summary>
          显示 {rangeStart}-{rangeEnd} 条记录，总共 {total} 条
        </Pagination.Summary>
        <Pagination.Content>
          <Pagination.Item>
            <Pagination.Previous
              isDisabled={page <= 1 || loading}
              onPress={() => {
                const next = Math.max(1, page - 1);
                setPage(next);
              }}
            >
              <Pagination.PreviousIcon />
              上一页
            </Pagination.Previous>
          </Pagination.Item>

          {pageItems.map((item, index) =>
            item === "ellipsis" ? (
              <Pagination.Item key={`ellipsis-${index}`}>
                <Pagination.Ellipsis />
              </Pagination.Item>
            ) : (
              <Pagination.Item key={item}>
                <Pagination.Link
                  isActive={item === page}
                  onPress={() => {
                    setPage(item);
                  }}
                >
                  {item}
                </Pagination.Link>
              </Pagination.Item>
            ),
          )}

          <Pagination.Item>
            <Pagination.Next
              isDisabled={page >= totalPages || loading}
              onPress={() => {
                const next = Math.min(totalPages, page + 1);
                setPage(next);
              }}
            >
              下一页
              <Pagination.NextIcon />
            </Pagination.Next>
          </Pagination.Item>
        </Pagination.Content>
      </Pagination>

      <Modal.Backdrop isOpen={resultOpen} onOpenChange={setResultOpen}>
        <Modal.Container size="cover">
          <Modal.Dialog className="max-w-3xl">
            {() => (
              <>
                <Modal.Header>
                  <Modal.Heading>查标结果</Modal.Heading>
                  <Modal.CloseTrigger />
                </Modal.Header>
                <Modal.Body className="flex flex-col gap-4">
                  {activeRecord && (
                    <div className="text-sm text-muted">
                      # {activeRecord.decode_id} · {activeRecord.file_name}
                    </div>
                  )}

                  {resultLoading ? (
                    <div className="flex flex-col items-center gap-3 py-10">
                      <Spinner />
                      <p className="text-sm text-muted">正在拉取查询结果…</p>
                    </div>
                  ) : resultError ? (
                    <div className="flex flex-col items-center gap-3 py-8 text-center">
                      <AlertCircle size={28} className="text-danger" />
                      <p className="text-sm text-danger">{resultError}</p>
                    </div>
                  ) : results.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 py-8 text-center">
                      <AlertCircle size={28} className="text-amber-500" />
                      <p className="text-base font-medium text-foreground">未找到数字标记</p>
                      <p className="text-sm text-muted">
                        没有发现有效的数字标记信息，或者您不是该模板的发货人
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 size={18} className="text-success" />
                        <h3 className="text-base font-medium text-foreground">
                          找到 {results.length} 条数字标记记录
                        </h3>
                        <Tooltip>
                          <Tooltip.Trigger>
                            <HelpCircle size={16} className="text-muted" />
                          </Tooltip.Trigger>
                          <Tooltip.Content>
                            <div>
                              <div className="mb-1 text-md font-medium text-primary">
                                为什么我能看见两条或更多的记录？
                              </div>
                              <span>
                                如果您的查询记录存在2条或以上的记录，意味着该模板中有某些图层来源于其他模板，结果仅供参考。
                              </span>
                            </div>
                          </Tooltip.Content>
                        </Tooltip>
                      </div>

                      <div className="space-y-3">
                        {results.map((result, index) => (
                          <div
                            key={`${result.template_id}-${index}`}
                            className="rounded-lg border border-border bg-default/40 p-4"
                          >
                            <div className="mb-3 flex items-start justify-between gap-3">
                              <h4 className="text-base font-medium text-foreground">
                                {result.template_title}
                              </h4>
                              <span className="shrink-0 rounded-full bg-primary/10 px-2 py-1 text-xs text-primary">
                                ID: {result.template_id}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                              <div className="flex items-center gap-2">
                                <User size={16} className="text-muted" />
                                <span className="text-muted">发送者:</span>
                                <ComponentDynamicUser uid={result.uid} />
                              </div>
                              <div className="flex items-center gap-2">
                                <User size={16} className="text-muted" />
                                <span className="text-muted">接收者:</span>
                                <ComponentDynamicUser uid={result.receiver_uid} />
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar size={16} className="text-muted" />
                                <span className="text-muted">发送时间:</span>
                                <span>
                                  {result.send_at ? (
                                    formatTime(result.send_at, "YYYY-MM-DD HH:mm:ss")
                                  ) : (
                                    <span className="text-muted">仅模板发送人和签收人可见</span>
                                  )}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock size={16} className="text-muted" />
                                <span className="text-muted">接收时间:</span>
                                <span>
                                  {result.received_at ? (
                                    formatTime(result.received_at, "YYYY-MM-DD HH:mm:ss")
                                  ) : (
                                    <span className="text-muted">仅模板发送人和签收人可见</span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button slot="close" variant="secondary">
                    关闭
                  </Button>
                </Modal.Footer>
              </>
            )}
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </div>
  );
}
